import { MetricSeriesResult } from 'dashboard409a/services/alexandria/types';
import { CompRef } from '../constants';
import { extractCompanyNames, groupPoints, MetricSeriesByCompany, withNtmRevenueGrowth } from './metrics';

export interface CompsSeriesResult {
  byCompany: MetricSeriesByCompany;
  companyNames: Record<string, string>;
  // A previous successful response remains available when a background
  // refetch fails. Callers use this to keep stale data visible instead of
  // replacing it with a full-section error.
  hasData: boolean;
  // isLoading = first load (no data yet); isFetching = refetch while the
  // previous window is still shown.
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  // When the data was last fetched (epoch ms, 0 before the first success) —
  // feeds the "Dashboard data fetched at h:mm" note beside the refresh button.
  dataUpdatedAt: number;
  // Comps with no usable data. Stats are computed without them, so callers
  // must tell the user.
  missingTickers: string[];
}

export interface CompsQueryState {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  dataUpdatedAt?: number;
}

// Pure merge of one batched response into the shape the widgets consume.
export const buildCompsSeriesResult = (
  comps: CompRef[],
  data: MetricSeriesResult | undefined,
  queryState: CompsQueryState
): CompsSeriesResult => {
  const points = data?.points ?? [];
  const byCompany = withNtmRevenueGrowth(groupPoints(points));
  const companyNames = extractCompanyNames(data?.companies ?? []);
  // While still loading, nothing is "missing" yet.
  const missingTickers = data ? comps.map(comp => comp.ticker).filter(ticker => !byCompany[ticker]) : [];

  return {
    byCompany,
    companyNames,
    hasData: data !== undefined,
    missingTickers,
    isLoading: queryState.isLoading,
    isFetching: queryState.isFetching,
    isError: queryState.isError,
    dataUpdatedAt: queryState.dataUpdatedAt ?? 0,
  };
};
